"use client";

import type { VideoCardData } from "../../types/insurabot-cards";

// Extract a YouTube video id from watch / share / embed URLs.
function youtubeId(url: string): string | null {
  if (!url) return null;
  const m = url.match(
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([\w-]{11})/,
  );
  return m?.[1] ?? null;
}

/**
 * Inline video card — plays the walkthrough INSIDE the chat (responsive 16:9),
 * instead of a link that pops a new tab. Supports YouTube URLs and direct .mp4.
 */
export default function VideoCard({ data }: { data: VideoCardData }) {
  const id = youtubeId(data.url);
  const isMp4 = /\.mp4($|\?)/i.test(data.url);

  return (
    <div className="rounded-xl border border-gray-200 overflow-hidden bg-black shadow-sm">
      <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
        {id ? (
          <iframe
            className="absolute inset-0 h-full w-full"
            src={`https://www.youtube.com/embed/${id}?rel=0&modestbranding=1`}
            title={data.title || "Video"}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        ) : isMp4 ? (
          <video
            className="absolute inset-0 h-full w-full"
            src={data.url}
            controls
            preload="metadata"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <a href={data.url} target="_blank" rel="noopener" className="text-sm text-white underline">
              Open video
            </a>
          </div>
        )}
      </div>
      {(data.title || data.durationLabel) && (
        <div className="flex items-center justify-between gap-2 bg-white px-3 py-2">
          {data.title && (
            <span className="truncate text-xs font-medium text-gray-800">{data.title}</span>
          )}
          {data.durationLabel && (
            <span className="flex-shrink-0 text-[11px] text-gray-500">{data.durationLabel}</span>
          )}
        </div>
      )}
    </div>
  );
}
